import { useState, useEffect } from 'react';
import { useCountry } from '../../contexts/CountryContext';
import { api } from '../../lib/api';
import { formatDate, formatDateTime, getStatusColor, WASTE_STREAM_LABELS } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Truck, FileText, Send } from 'lucide-react';

export function ServiceRequestsPage() {
  const { countries, selectedCountry } = useCountry();
  const [requests, setRequests] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    store_id: '',
    waste_stream: '',
    description: '',
    priority: 'normal',
    required_by_date: '',
    requestor_name: '',
  });

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedCountry ? { country_code: selectedCountry } : {};
      const [requestsData, storesData] = await Promise.all([
        api.getServiceRequests(params),
        api.getStores(params),
      ]);
      setRequests(requestsData);
      setStores(storesData);
    } catch (error) {
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    try {
      const store = stores.find(s => s.id === formData.store_id);
      await api.createServiceRequest({
        ...formData,
        country_code: store?.country_code || selectedCountry || 'NL',
        required_by_date: new Date(formData.required_by_date).toISOString(),
        fexa_wo_id: `FEXA-${Date.now()}`, // Mock Fexa WO ID
      });
      toast.success('Service request created (Fexa sync simulated)');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create service request');
    }
  };

  const getStoreName = (id) => stores.find(s => s.id === id)?.name || '-';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="service-requests-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Service Requests</h1>
          <p className="text-muted-foreground mt-1">
            Fexa integration - manage customer work orders
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-request-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create Service Request</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateRequest} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Store *</Label>
                  <Select
                    value={formData.store_id}
                    onValueChange={(value) => setFormData({ ...formData, store_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Waste Stream *</Label>
                  <Select
                    value={formData.waste_stream}
                    onValueChange={(value) => setFormData({ ...formData, waste_stream: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(WASTE_STREAM_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Required By *</Label>
                  <Input
                    type="date"
                    value={formData.required_by_date}
                    onChange={(e) => setFormData({ ...formData, required_by_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Requestor</Label>
                  <Input
                    value={formData.requestor_name}
                    onChange={(e) => setFormData({ ...formData, requestor_name: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Request</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Fexa Integration Notice */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6 text-blue-600" />
            <div>
              <p className="font-medium text-blue-900">Fexa Integration (Mocked)</p>
              <p className="text-sm text-blue-700">
                Service requests sync with Fexa facilities management platform. This demo uses simulated data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fexa WO#</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Waste Stream</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Required By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Truck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No service requests found</p>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell className="font-mono text-sm">{req.fexa_wo_id || '-'}</TableCell>
                    <TableCell className="font-medium">{getStoreName(req.store_id)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {WASTE_STREAM_LABELS[req.waste_stream] || req.waste_stream}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          req.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                          req.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }
                      >
                        {req.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(req.required_by_date)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(req.status)}>
                        {req.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {req.status === 'new' && (
                        <Button size="sm" variant="outline">
                          <Send className="w-4 h-4 mr-1" />
                          Send RFQ
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
